import { useEffect, useRef, useState } from "react";
import { ApiError, api, get } from "../lib/api.ts";
import { navigate } from "../lib/router.ts";
import { useSession } from "../lib/session.tsx";
import type { Tenant } from "../lib/types.ts";
import { COMPANY_HEAD, Field, Showcase, Steps } from "./auth.tsx";

/**
 * A store comes from a company: CNPJ and razão social (looked up at the
 * Receita through BrasilAPI), plus the name people see. The first store is
 * step 3 of signing up, in the sign-up card; later stores use the same form
 * inside the app.
 */

interface Company {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  situacao: string | null;
  ativa: boolean;
}
type Lookup = { found: true; company: Company } | { found: false; reason: "not_found" | "unavailable" };

const digits = (s: string) => s.replace(/\D/g, "").slice(0, 14);
const mask = (d: string) =>
  d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");

export function cnpjValid(s: string): boolean {
  const d = digits(s);
  if (d.length !== 14 || /^(\d)\1+$/.test(d)) return false;
  const dv = (n: number) => {
    const w = n === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = w.reduce((a, x, i) => a + x * Number(d[i]), 0) % 11;
    return sum < 2 ? 0 : 11 - sum;
  };
  return dv(12) === Number(d[12]) && dv(13) === Number(d[13]);
}

/** Same rule as the server: "PADARIA PÃO DOURADO LTDA" → "Padaria Pão Dourado". */
function nameFrom(razao: string): string {
  const small = new Set(["da", "de", "do", "das", "dos", "e"]);
  return razao
    .trim()
    .replace(/\s+(LTDA|ME|EPP|EIRELI|S\/?A|MEI|SLU)\.?$/i, "")
    .toLowerCase()
    .split(/\s+/)
    .map((w, i) => (i > 0 && small.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ")
    .slice(0, 100);
}

const titleCase = (s: string) => (s === s.toUpperCase() ? nameFrom(s) : s);

export function NewStore({ first }: { first: boolean }) {
  const form = <CompanyForm first={first} />;
  if (!first) {
    return (
      <div style={{ maxWidth: 520 }}>
        <div className="topbar">
          <h1>Nova loja</h1>
        </div>
        <div className="card">{form}</div>
      </div>
    );
  }
  return <FirstStore>{form}</FirstStore>;
}

/** Step 3 of signing up, in the same card as steps 1 and 2. */
function FirstStore({ children }: { children: React.ReactNode }) {
  const s = useSession();
  return (
    <div className="au">
      <div className="au-card">
        <Showcase head={COMPANY_HEAD} />
        <section className="au-form">
          <div>
            <h1>Sua empresa</h1>
            <p className="au-lead">Olá, {s.account.name.split(" ")[0]}. Com o CNPJ preenchemos a razão social para você.</p>
          </div>
          <Steps at={2} />
          {children}
          <p className="au-legal">
            Entrou com {s.account.email}.{" "}
            <button type="button" className="linkish" onClick={() => s.logout()}>
              Sair
            </button>
          </p>
        </section>
      </div>
    </div>
  );
}

function CompanyForm({ first }: { first: boolean }) {
  const s = useSession();
  const [cnpj, setCnpj] = useState("");
  const [razao, setRazao] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState<{ kind: "busy" | "ok" | "warn"; text: string } | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // What the lookup filled in, so a later lookup replaces it but never what the person typed.
  const filled = useRef({ razao: "", name: "" });
  const looked = useRef("");

  const d = digits(cnpj);
  useEffect(() => {
    if (d.length < 14) {
      setStatus(null);
      looked.current = "";
      return;
    }
    if (!cnpjValid(d)) {
      setStatus(null);
      setErrors((e) => ({ ...e, cnpj: "Este CNPJ não confere. Revise os dígitos." }));
      return;
    }
    if (looked.current === d) return;
    looked.current = d;
    setStatus({ kind: "busy", text: "Consultando" });
    setHint(null);
    let live = true;
    get<Lookup>(`/v1/cnpj/${d}`, { tenant: false })
      .then((r) => {
        if (!live) return;
        if (!r.found) {
          setStatus(null);
          setHint(
            r.reason === "not_found"
              ? "Não achamos este CNPJ na Receita. Confira os dígitos, ou digite a razão social."
              : "A consulta ao CNPJ está fora do ar agora. Digite a razão social.",
          );
          return;
        }
        const c = r.company;
        setStatus(c.ativa ? { kind: "ok", text: "Ativa" } : { kind: "warn", text: c.situacao ?? "Inativa" });
        if (!c.ativa) setHint(`Na Receita, este CNPJ está ${(c.situacao ?? "inativo").toLowerCase()}. Confira antes de continuar.`);
        setRazao((cur) => (cur === "" || cur === filled.current.razao ? c.razaoSocial : cur));
        filled.current.razao = c.razaoSocial;
        const suggested = c.nomeFantasia ? titleCase(c.nomeFantasia) : nameFrom(c.razaoSocial);
        setName((cur) => (cur === "" || cur === filled.current.name ? suggested : cur));
        filled.current.name = suggested;
      })
      .catch((err) => {
        if (!live) return;
        setStatus(null);
        setHint(err instanceof ApiError ? err.message : "Sem conexão. Digite a razão social.");
      });
    return () => {
      live = false;
    };
  }, [d]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const bad: Record<string, string> = {};
    if (!cnpjValid(d)) bad.cnpj = d.length < 14 ? "Digite os 14 dígitos do CNPJ." : "Este CNPJ não confere. Revise os dígitos.";
    if (!razao.trim()) bad.razaoSocial = "Informe a razão social.";
    setErrors(bad);
    if (Object.keys(bad).length) return;
    setBusy(true);
    try {
      const body = { cnpj: d, razaoSocial: razao.trim(), ...(name.trim() ? { name: name.trim() } : {}) };
      const { tenant } = await api<{ tenant: Tenant }>("POST", "/v1/tenants", body, { tenant: false });
      await s.reloadTenants();
      s.switchTenant(tenant.id);
      navigate("/");
      s.toast(first ? "Loja criada. Seus 14 dias grátis começaram hoje." : `Loja ${tenant.name} criada.`);
    } catch (err) {
      const by: Record<string, string> = {};
      if (err instanceof ApiError) for (const x of err.details) by[String(x.path[0])] = x.message;
      if (!Object.keys(by).length) by.razaoSocial = err instanceof Error ? err.message : "Erro";
      setErrors(by);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="au-fields" noValidate onSubmit={submit}>
      <Field id="cnpj" label="CNPJ" error={errors.cnpj}>
        <span className="au-box">
          <input
            id="cnpj"
            className="mono"
            inputMode="numeric"
            autoComplete="off"
            placeholder="00.000.000/0000-00"
            value={cnpj}
            autoFocus
            onChange={(e) => {
              setCnpj(mask(digits(e.target.value)));
              setErrors(({ cnpj: _, ...rest }) => rest);
            }}
          />
          {status && (
            <span className={`au-stat ${status.kind}`}>
              {status.kind === "busy" && <i />}
              {status.text}
            </span>
          )}
        </span>
      </Field>
      <Field id="razao" label="Razão social" error={errors.razaoSocial}>
        <input id="razao" autoComplete="organization" placeholder="Preenchida pelo CNPJ" value={razao} onChange={(e) => setRazao(e.target.value)} />
      </Field>
      <Field id="nome" label="Nome da loja" error={errors.name}>
        <input id="nome" placeholder="Como seus clientes conhecem a loja" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      {hint && <p className="au-legal">{hint}</p>}
      <button className={`btn primary au-go ${busy ? "loading" : ""}`} disabled={busy}>
        {busy ? "Criando sua loja…" : first ? "Criar minha loja" : "Criar loja"}
      </button>
    </form>
  );
}
